import { useContext, useEffect, useState } from 'react'
import { UIContext } from 'Context/UI'
import { useNavigate } from 'react-router-dom'
import { useProfileStore } from 'Store/Profile'
import { useFileDownloadStore } from 'Store/FileDownload'
import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import { extractFileName } from 'Utils/extractFileName'
import { generateDownloadUrl } from 'Utils/generateDownloadUrl'

import { TABS } from '../Config'
import IndustrialType from 'Data/industrial-type.json'

const INIITAL_STATE_UPLOAD_FILE = {
	companyVatRegistrationFile: false,
	companyCertificateFile: false,
	companyIsoFile: false,
	companyOtherFile: false,
}

const VIEW_INDEX = 2

const ViewModel = () => {
	const validationSchema = Yup.object().shape({
		supplierType: Yup.string().required('กรุณาเลือกประเภทร้านค้า'),
		companyName: Yup.string().required('กรุณากรอกชื่อบริษัท/ชื่อเจ้าของกิจการ'),
		companyTaxNo: Yup.string()
			.test('TestMaxLength', 'กรุณากรอกเลขประจำตัวผู้เสียภาษีอากรให้ครบ 13 หลัก', value => {
				if ((value ?? '').replaceAll('-', '').length === 13) {
					return true
				} else {
					return false
				}
			})
			.required('กรุณากรอกเลขประจำตัวผู้เสียภาษีอากร'),
	})

	const navigate = useNavigate()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const [formState, setFormState] = useState({
		id: 0,
		code: '',
		slug: '',
		displayTitle: '',
		contactPersonFullname: '',
		contactPersonPosition: '',
		supplierType: '',
		businessDescription: '',
		email: '',
		website: '',
		companyName: '',
		companyAddress: '',
		companyTaxNo: '',
		minimumProductionQuantity: '',
		totalEmployee: 0,
		companyPictureCover: null,
		companyLogo: null,
		companyCard: null,
		companyFactoryPicture: null,
		companyVatRegistrationFile: null,
		companyCertificateFile: null,
		companyIsoFile: null,
		companyOtherFile: null,
	})
	const { profile, updateProgress, setUpdateProgress, getProfile, updateProfile } = useProfileStore(
		state => ({
			profile: state.profile,
			updateProgress: state.updateProgress,
			getProfile: state.getProfile,
			updateProfile: state.updateProfile,
			setUpdateProgress: state.setUpdateProgress,
		}),
		shallow
	)
	const [hasChanged, setHasChanged] = useState(false)
	const [error, setError] = useState<{ [key: string]: string }>({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const supplierTypeOptions = IndustrialType.map(type => ({
		label: `${type.code} ${type.nameTh} (${type.nameEn})`,
		value: type.code,
	}))
	const [hasUploadFile, setHasUploadFile] = useState(INIITAL_STATE_UPLOAD_FILE)
	const downloadFile = useFileDownloadStore(state => state.download)

	const [uploadFileKey, setUploadFileKey] = useState('')

	useCallbackPrompt(hasChanged)

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const onChangeFile = (name: string, file: any) => {
		if (
			[
				'companyVatRegistrationFile',
				'companyCertificateFile',
				'companyIsoFile',
				'companyOtherFile',
			].includes(name)
		) {
			setUploadFileKey(name)
			updateProfile({
				[name]: file,
			}).then(({ success, data }) => {
				if (!success) {
					toast.error(data ?? `อัพโหลดเอกสารไม่สำเร็จ`)
					setUploadFileKey('')
					setUpdateProgress(0)
					return
				} else {
					getProfile().then(({ success: successP, data: pData }) => {
						if (successP) {
							setUpdateProgress(0)
							setFormState(pData)
							setHasChanged(false)
							toast.success('บันทึกข้อมูลสำเร็จ')
							setIsSubmitting(false)
							setUploadFileKey('')
						} else if (!successP) {
							toast.error('ดึงข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
							setHasChanged(false)
							setIsSubmitting(false)
							setUploadFileKey('')
							setUpdateProgress(0)
						}
					})
				}
			})
		}
	}

	const onChange = (name: string, value: any) => {
		setFormState({
			...formState,
			[name]: value,
		})
		setHasChanged(true)

		if (
			[
				'companyVatRegistrationFile',
				'companyCertificateFile',
				'companyIsoFile',
				'companyOtherFile',
			].includes(name)
		) {
			setHasUploadFile({
				...hasUploadFile,
				[name]: true,
			})
		} else {
			validationSchema
				.validate(
					{
						...formState,
						[name]: value,
					},
					{ abortEarly: false }
				)
				.then(() => {
					setError({})
				})
				.catch(err => {
					setError(
						err.inner.reduce((all: any[], e: any) => {
							all[e.path] = e.message

							return all
						}, {})
					)
				})
		}
	}

	const onDownload = (key: string) => {
		// @ts-ignore
		const filePath = profile[key]
		if (!filePath) return
		else {
			downloadFile(
				generateDownloadUrl(filePath),
				extractFileName(generateDownloadUrl(filePath))
			).then(({ success, data }) => {
				if (!success) {
					Swal.fire({
						icon: 'error',
						title: 'ดาวน์โหลดไฟล์ไม่สำเร็จ',
						text: data,
					})
				}
			})
		}
	}

	const onSubmitInformation = async () => {
		if (hasChanged) {
			setIsSubmitting(true)
			const body: { [key: string]: any } = {}

			const requiredFields = ['supplierType', 'companyName', 'companyTaxNo']
			const allFields = [...requiredFields, 'companyAddress']

			validationSchema
				.validate(formState, { abortEarly: false })
				.then(() => {
					setError({})

					allFields.forEach(field => {
						if (field === 'companyTaxNo') {
							if (formState[field].replaceAll('-', '') !== profile[field]) {
								body[field] = formState[field].replaceAll('-', '')
							}
						}
						// @ts-ignore
						else if (formState[field] !== profile[field]) {
							// @ts-ignore
							body[field] = formState[field]
						}
					})

					if (Object.keys(body).length > 0) {
						updateProfile(body).then(({ success, data }) => {
							if (!success) {
								toast.error(`บันทึกข้อมูลไม่สำเร็จ ${data}`)
								setIsSubmitting(false)
								return
							} else {
								getProfile().then(({ success: successP, data: pData }) => {
									if (successP) {
										setFormState(pData)
										setHasChanged(false)
										toast.success('บันทึกข้อมูลสำเร็จ')
										setIsSubmitting(false)
									} else if (!successP) {
										toast.error('ดึงข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
										setHasChanged(false)
										setIsSubmitting(false)
									}
								})
							}
						})
					} else {
						setIsSubmitting(false)
					}
				})
				.catch(err => {
					toast.error('กรุณากรอกข้อมูลให้ครบถ้วน')
					setError(
						err.inner.reduce((all: any[], e: any) => {
							all[e.path] = e.message

							return all
						}, {})
					)
					setIsSubmitting(false)
				})
		}
	}

	const onSubmitAttachFile = async () => {
		if (hasChanged) {
			setIsSubmitting(true)
			const body: { [key: string]: any } = {}

			const allFields = [
				'companyCertificateFile',
				'companyVatRegistrationFile',
				'companyIsoFile',
				'companyOtherFile',
			]

			allFields.forEach(field => {
				// @ts-ignore
				if (formState[field] !== profile[field]) {
					// @ts-ignore
					body[field] = formState[field]
				}
			})

			if (Object.keys(body).length > 0) {
				updateProfile(body).then(({ success, data }) => {
					if (!success) {
						toast.error(data)
						setIsSubmitting(false)
						return
					} else {
						getProfile().then(({ success: successP, data: pData }) => {
							if (successP) {
								setFormState(pData)
								setHasChanged(false)
								toast.success('บันทึกข้อมูลสำเร็จ')
								setIsSubmitting(false)
								setHasUploadFile(INIITAL_STATE_UPLOAD_FILE)
							} else if (!successP) {
								toast.error('ดึงข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
								setHasChanged(false)
								setIsSubmitting(false)
							}
						})
					}
				})
			} else {
				setIsSubmitting(false)
			}
		}
	}

	const onSubmitEmployee = async () => {
		if (hasChanged) {
			setIsSubmitting(true)
			const body: { [key: string]: any } = {}

			const allFields = ['totalEmployee', 'minimumProductionQuantity']

			allFields.forEach(field => {
				// @ts-ignore
				if (formState[field] !== profile[field]) {
					// @ts-ignore
					body[field] = formState[field]
				}
			})

			if (Object.keys(body).length > 0) {
				updateProfile(body).then(({ success, data }) => {
					if (!success) {
						toast.error(data)
						setIsSubmitting(false)
						return
					} else {
						getProfile().then(({ success: successP, data: pData }) => {
							if (successP) {
								setFormState(pData)
								setHasChanged(false)
								toast.success('บันทึกข้อมูลสำเร็จ')
								setIsSubmitting(false)
							} else if (!successP) {
								toast.error('ดึงข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
								setHasChanged(false)
								setIsSubmitting(false)
							}
						})
					}
				})
			} else {
				setIsSubmitting(false)
			}
		}
	}

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setFormState(profile)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		companyName: profile?.companyName,
		currentTab: VIEW_INDEX,
		onChangeTab,
		formState,
		uploadFileKey,
		updateProgress,
		onChange,
		supplierTypeOptions,
		onSubmitInformation,
		onSubmitAttachFile,
		onSubmitEmployee,
		error,
		isSubmitting,
		hasUploadFile,
		onDownload,
		onChangeFile,
	}
}

export default ViewModel
